import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'motion/react'
import { CalendarClock, ChevronDown, Gamepad2, SlidersHorizontal, Trophy, UserPlus, Zap } from 'lucide-react'
import { modeIcon, modeLabel } from '../utils/teamStats'
import {
  ACTIVITY_VALUES, AGE_VALUES, CATALOG_MODE_KINDS, LEVEL_VALUES, SORT_VALUES,
  activeFilterCount, activityLabel, ageLabel, levelLabel, sortLabel, toggleMode
} from '../utils/teamCatalogFilters'
import { CARD_CLASS } from '../utils/teamUi'
import { hapticImpact } from '../../../utils/haptic'
import useAppStore from '../../../store/useAppStore'

const CHIP_BASE = 'flex shrink-0 items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-semibold transition-all active:scale-[0.98]'
const CHIP_ON = 'border-accent-primary/40 bg-accent-primary/10 text-accent-primary'
const CHIP_OFF = 'border-white/10 bg-white/[0.03] text-text-secondary'

/** Группа чипов с переносом на следующие строки */
const ChipRow = ({ children }) => (
  <div className="flex flex-wrap gap-2">
    {children}
  </div>
)

const Group = ({ icon: Icon, title, children }) => (
  <div>
    <p className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-text-secondary">
      <Icon size={12} className="text-accent-primary" />
      {title}
    </p>
    <ChipRow>{children}</ChipRow>
  </div>
)

/**
 * Фильтры каталога банд (docs/team-api.md §2).
 *
 * Всегда на виду только сортировка и «Есть места», остальное — в сворачиваемой
 * панели: на мобильном шесть групп чипов вытеснили бы сам каталог.
 * Числовые пороги уровней и окон активности в подписях не показываем — их
 * меняют в админке без выката фронта.
 *
 * @param {object} filters - {level, modes, age, activity, free, sort}
 * @param {function} onChange - патч фильтров; страница сама сбрасывает page на 1
 */
const TeamCatalogFilters = ({ filters, onChange }) => {
  const { t } = useTranslation()
  const minesEnabled = useAppStore((state) => state.user?.mines_enabled !== false)
  const [isOpen, setIsOpen] = useState(false)

  const count = activeFilterCount(filters)

  const patch = (next) => {
    hapticImpact('light')
    onChange(next)
  }

  const chipClass = (isActive) => `${CHIP_BASE} ${isActive ? CHIP_ON : CHIP_OFF}`

  return (
    <div className="space-y-3">
      {/* Всегда на виду: кнопка панели и «Есть места» */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            hapticImpact('light')
            setIsOpen((open) => !open)
          }}
          className={chipClass(isOpen || count > 0)}
          aria-expanded={isOpen}
        >
          <SlidersHorizontal size={13} />
          {t('team.filters.title', 'Фильтры')}
          {count > 0 && (
            <span className="rounded-full bg-accent-primary px-1.5 text-[10px] font-bold leading-4 tabular-nums text-dark-primary">
              {count}
            </span>
          )}
          <ChevronDown size={13} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        <button
          type="button"
          onClick={() => patch({ free: !filters.free })}
          className={chipClass(filters.free)}
        >
          <UserPlus size={13} />
          {t('team.filters.free', 'Есть места')}
        </button>
      </div>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className={`${CARD_CLASS} space-y-4 p-4`}>
              <Group icon={Trophy} title={t('team.filters.levelTitle', 'Уровень')}>
                {[null, ...LEVEL_VALUES].map((value) => (
                  <button
                    key={value || 'any'}
                    type="button"
                    onClick={() => patch({ level: value })}
                    className={chipClass(filters.level === value)}
                  >
                    {levelLabel(value, t)}
                  </button>
                ))}
              </Group>

              {/* Мультивыбор: в выдачу попадёт банда, у которой хотя бы один режим в топ-3 */}
              <Group icon={Gamepad2} title={t('team.filters.modesTitle', 'Во что играют')}>
                {CATALOG_MODE_KINDS.filter((kind) => kind !== 'mines' || minesEnabled).map((kind) => {
                  const Icon = modeIcon(kind)
                  return (
                    <button
                      key={kind}
                      type="button"
                      onClick={() => patch({ modes: toggleMode(filters.modes, kind) })}
                      className={chipClass(filters.modes.includes(kind))}
                    >
                      <Icon size={13} />
                      {modeLabel(kind, t)}
                    </button>
                  )
                })}
              </Group>

              <Group icon={CalendarClock} title={t('team.filters.ageTitle', 'Когда создана')}>
                {[null, ...AGE_VALUES].map((value) => (
                  <button
                    key={value || 'any'}
                    type="button"
                    onClick={() => patch({ age: value })}
                    className={chipClass(filters.age === value)}
                  >
                    {ageLabel(value, t)}
                  </button>
                ))}
              </Group>

              {/* Активность — только по совместным играм банды (2+ участника в раунде или сессии) */}
              <Group icon={Zap} title={t('team.filters.activityTitle', 'Активность')}>
                {[...ACTIVITY_VALUES, null].map((value) => (
                  <button
                    key={value || 'all'}
                    type="button"
                    onClick={() => patch({ activity: value })}
                    className={chipClass(filters.activity === value)}
                  >
                    {activityLabel(value, t)}
                  </button>
                ))}
              </Group>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Сортировка */}
      <ChipRow>
        {SORT_VALUES.map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => patch({ sort: value })}
            className={chipClass(filters.sort === value)}
          >
            {sortLabel(value, t)}
          </button>
        ))}
      </ChipRow>
    </div>
  )
}

export default TeamCatalogFilters
